import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ComponentRef,
	inject,
	Input,
	OnDestroy,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { ICmmsSelectedFilter } from '@app/pages/cmms/components/cmms-sort-filters-grouping/interfaces-and-types/filter.interfaces';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { FilterTypes } from '@app/pages/cmms/components/cmms-sort-filters-grouping/interfaces-and-types/filter.types';
import { TextFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/text-filter/text-filter.component';
import { ComponentType } from '@angular/cdk/portal';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
	isICmmsFilterWithDataSetter,
	isICmmsFilterWithDefaultData,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StatusFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/status-filter/status-filter.component';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { LocationTreeFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/location-tree-filter/location-tree-filter.component';
import { PriorityFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/priority-filter/priority-filter.component';
import { CountRangeFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/count-range-filter/count-range-filter.component';
import { DateRangeFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/date-range-filter/date-range-filter.component';
import { AssigneeFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/assignee-filter/assignee-filter.component';
import { AuUtilsFunctions } from '@app/shared';
import { MultitextFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/multitext-filter/multitext-filter.component';
import { BinaryFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/binary-filter/binary-filter.component';
import { MultiselectFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/multiselect-filter/multiselect-filter.component';
import { ClassificationFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/classification-filter/classification-filter.component';
import { FileDocumentTypeFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/file-document-type-filter/file-document-type-filter.component';
import { IfcFilterComponent } from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/ifc-filter/ifc-filter.component';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { DateIntervalFilterComponent } from '@app/shared/filter-components/date-interval/date-interval-filter.component';

@Component({
	selector: 'applied-filter-button',
	templateUrl: './applied-filter-button.component.html',
	styleUrls: ['./applied-filter-button.component.scss'],
	imports: [CommonModule, MatMenuModule, MatButtonModule, MatIconModule, NgxTolgeeModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppliedFilterButtonComponent implements AfterViewInit, OnDestroy {
	private readonly filterService = inject(CmmsFiltersService);
	private readonly cdr = inject(ChangeDetectorRef);

	@Input() filter: ICmmsSelectedFilter;
	@Input() activePage: CmmsPages;
	@Input() currentEntity:
		| ICmmsAsset[]
		| IWorkOrder[]
		| IRequest[]
		| IIncident[]
		| IFileEntity[];

	@ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
	@ViewChild('noFilterTemplate', { read: TemplateRef })
	noFilterTemplateRef: TemplateRef<any>;
	/* Keys in object should be the same as filter type listed in FilterTypes */
	filterComponentsByType: Record<FilterTypes, ComponentType<ICmmsFilterComponent>> = {
		text: TextFilterComponent,
		multitext: MultitextFilterComponent,
		status: StatusFilterComponent,
		multiselect: MultiselectFilterComponent,
		classification: ClassificationFilterComponent,
		location: LocationTreeFilterComponent,
		binary: BinaryFilterComponent,
		priority: PriorityFilterComponent,
		countRange: CountRangeFilterComponent,
		date: DateRangeFilterComponent,
		assignee: AssigneeFilterComponent,
		documentType: FileDocumentTypeFilterComponent,
		bim: IfcFilterComponent,
		dateInterval: DateIntervalFilterComponent,
	};
	buttonIsActive = false;
	filterValue: ICmmsFilterEmittedValue;
	filterComponentRef: ComponentRef<ICmmsFilterComponent>;

	// To emit event for filter only when the menu is closed
	// We need to check that the value differs from the one before opening
	// So we store here the filterValue on menu open
	filterValueToCheck: ICmmsFilterEmittedValue;

	@ViewChild('filterTemplate', { read: ViewContainerRef })
	private filterTemplateRef: ViewContainerRef;
	private destroy$ = new Subject<void>();

	ngAfterViewInit(): void {
		if (this.filter.autoFocus) {
			this.trigger.openMenu();
		}
		this.buttonIsActive = this.filter.autoFocus;
		this.cdr.detectChanges();
		this.loadFilterComponent(this.filter.selectedFilter.type);
	}

	removeFilter(filter: ICmmsSelectedFilter): void {
		this.filterService.removeFilterFromSelection(filter);
	}

	menuOpened(): void {
		this.buttonIsActive = true;
		this.filterValueToCheck = this.filterValue?.data;
		if (
			this.filterComponentRef.instance &&
			isICmmsFilterWithDataSetter(this.filterComponentRef.instance) &&
			this.currentEntity
		) {
			this.filterComponentRef.instance.setFilterValue(this.currentEntity);
		}
	}

	menuIsClosed(): void {
		this.buttonIsActive = false;

		if (this.filterDataChanged() && this.filter.selectedFilter.type !== 'date') {
			this.filterService.updateSelectedFilterValue(
				this.filterValue.columnName,
				this.filterValue.data
			);
		} else if (this.filterDataChanged() && this.filter.selectedFilter.type === 'date') {
			const columnName = this.filter.selectedFilter.columnName;
			this.filterService.updateSelectedFilterValueFromObject({
				[`${columnName}After`]: `${this.filterValue.data.start}T00:00:00`,
				[`${columnName}Before`]: `${
					this.filterValue.data.end
						? this.filterValue.data.end
						: this.filterValue.data.start
				}T23:59:59`,
			});
		}
	}

	filterDataChanged(): boolean {
		return AuUtilsFunctions.isNotEqual(this.filterValue?.data, this.filterValueToCheck);
	}

	loadFilterComponent(filterType: FilterTypes) {
		const component: ComponentType<ICmmsFilterComponent> =
			this.filterComponentsByType[filterType];

		if (!component) {
			this.filterTemplateRef.createEmbeddedView(this.noFilterTemplateRef);
			return;
		}
		this.filterComponentRef = this.filterTemplateRef.createComponent(component);

		this.filterComponentRef.instance.data = {
			activePage: this.activePage,
			columnName: this.filter.selectedFilter.columnName,
			options: this.filter.selectedFilter.options,
			preselectedValue: this.filter.selectedFilter.preselectedValue,
		};

		// Make sure you subscribe to check for changes to filter values before dropping values into filters.
		// This is necessary to ensure that the preselected filters for the view work correctly.
		this.trackFilterSelection(this.filterComponentRef);
		if (this.filter.selectedFilter.preselectedValue !== undefined) {
			this.filterComponentRef.instance.ngOnInit();
		}

		if (
			!this.filter.autoFocus &&
			isICmmsFilterWithDefaultData(this.filterComponentRef.instance) &&
			this.currentEntity
		) {
			this.filterComponentRef.instance
				.getDefaultFilterValue(this.currentEntity)
				.subscribe((value: ICmmsFilterEmittedValue) => {
					this.filterValue = value;
					this.filterService.updateSelectedFilterValue(
						this.filter.selectedFilter.columnName,
						this.filterValue.data
					);
					this.cdr.detectChanges();
				});
		}
	}

	private trackFilterSelection(componentRef: ComponentRef<ICmmsFilterComponent>): void {
		componentRef.instance.filterValueChanged
			.pipe(takeUntil(this.destroy$))
			.subscribe(filterValue => {
				this.filterValue = filterValue;
				this.cdr.detectChanges();
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
